'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  Calendar,
  MapPin,
  Ticket,
  Plus,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Search,
  LayoutGrid,
  List,
  Users,
  X,
  Sparkles,
  CalendarDays,
  Globe,
} from 'lucide-react';
import Link from 'next/link';
import { eventsApi, getApiErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';
import { useAuthReady } from '@/lib/use-auth-ready';
import { canUseOrganizerDashboardApis } from '@/lib/organizer-access';
import { getEffectiveRole } from '@/lib/rbac';
import {
  OrganizerErrorState,
  OrganizerLoadingState,
} from '@/components/organizer/OrganizerPageStates';
import { resolveStorageUrl } from '@/lib/storage-url';

function eventKey(event: any): string {
  return String(event?.event_id ?? event?.id ?? event?.slug ?? event?.title ?? 'event');
}

function getEventCoverUrl(event: any): string | null {
  if (!event) return null;
  const raw =
    event.banner ||
    event.cover_image ||
    event.banner_url ||
    event.cover_image_url ||
    event.poster_url ||
    event.poster ||
    event.banner_path ||
    event.thumbnail ||
    event.thumbnail_url ||
    event.settings?.branding?.banner_override ||
    event.settings?.branding?.cover_override ||
    event.settings?.banner ||
    event.settings?.cover_image ||
    event.media?.banner ||
    event.media?.cover_image ||
    null;

  if (!raw || typeof raw !== 'string' || !raw.trim()) return null;
  return resolveStorageUrl(raw) ?? (raw.trim() || null);
}

type PaginationMeta = {
  current_page: number;
  last_page: number;
  total: number;
  per_page: number;
};

type FilterType = 'all' | 'active' | 'draft' | 'past';
type SortOption = 'date' | 'newest' | 'registrations' | 'title';
type ViewMode = 'grid' | 'list';

const CARD_GRADIENTS = [
  {
    // Deep Emerald & Forest Teal (EnthiosHub primary brand)
    bg: 'linear-gradient(135deg, #0d3b32 0%, #155e54 48%, #1f8a7a 100%)',
    accent: '#2dd4bf',
  },
  {
    // Royal Indigo & Electric Blue
    bg: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 48%, #4f46e5 100%)',
    accent: '#818cf8',
  },
  {
    // Deep Ocean & Cyan
    bg: 'linear-gradient(135deg, #0c4a6e 0%, #0369a1 48%, #0284c7 100%)',
    accent: '#38bdf8',
  },
  {
    // Modern Charcoal & Slate
    bg: 'linear-gradient(135deg, #1e293b 0%, #334155 48%, #475569 100%)',
    accent: '#94a3b8',
  },
  {
    // Vivid Midnight Violet
    bg: 'linear-gradient(135deg, #3b0764 0%, #581c87 48%, #7e22ce 100%)',
    accent: '#c084fc',
  },
  {
    // Sunset Copper & Terracotta (Rich, vibrant amber sunset)
    bg: 'linear-gradient(135deg, #7c2d12 0%, #9a3412 48%, #ea580c 100%)',
    accent: '#fb923c',
  },
  {
    // Deep Berry Rose
    bg: 'linear-gradient(135deg, #831843 0%, #9d174d 48%, #db2777 100%)',
    accent: '#f472b6',
  },
  {
    // Dark Pine & Mint
    bg: 'linear-gradient(135deg, #064e3b 0%, #047857 48%, #059669 100%)',
    accent: '#34d399',
  },
];

function getCardGradient(seed: string) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return CARD_GRADIENTS[Math.abs(hash) % CARD_GRADIENTS.length];
}

function EventCardCover({
  event,
  date,
  statusClass,
  statusLabel,
  isLive,
}: {
  event: any;
  date: { month: string; day: string };
  statusClass: string;
  statusLabel: string;
  isLive: boolean;
}) {
  const [hasError, setHasError] = useState(false);
  const id = eventKey(event);
  const palette = getCardGradient(id);
  const coverUrl = useMemo(() => getEventCoverUrl(event), [event]);
  const showImage = Boolean(coverUrl && !hasError);

  return (
    <div className="oe-card-cover">
      {showImage ? (
        <img
          src={coverUrl!}
          alt={event.title || 'Event cover'}
          className="oe-card-cover-bg"
          loading="lazy"
          onError={() => setHasError(true)}
        />
      ) : (
        <div
          className="oe-card-cover-gradient"
          style={{ background: palette.bg }}
        >
          {/* Subtle luminous ambient glow */}
          <div
            className="absolute -right-10 -top-10 h-32 w-32 rounded-full blur-2xl opacity-40 pointer-events-none"
            style={{ background: palette.accent }}
          />
          {/* Abstract geometric conference pattern */}
          <svg
            className="oe-card-cover-pattern"
            viewBox="0 0 400 130"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <circle cx="350" cy="20" r="85" fill="white" fillOpacity="0.12" />
            <circle cx="40" cy="115" r="65" fill="white" fillOpacity="0.08" />
            <path
              d="M-20 65 Q 160 130, 420 30"
              stroke="white"
              strokeWidth="2"
              strokeOpacity="0.18"
              fill="none"
            />
            <path
              d="M-10 100 Q 180 40, 420 85"
              stroke="white"
              strokeWidth="1.2"
              strokeOpacity="0.10"
              fill="none"
            />
          </svg>
        </div>
      )}
      <div
        className={`oe-card-cover-overlay ${showImage ? 'oe-card-cover-overlay--photo' : 'oe-card-cover-overlay--gradient'}`}
      />

      {/* Floating Date Badge */}
      <div className="oe-date-badge" aria-hidden>
        <span className="oe-date-month">{date.month}</span>
        <span className="oe-date-day">{date.day}</span>
      </div>

      {/* Floating Status Pill */}
      <div className={`oe-status-pill ${statusClass}`}>
        <span className={`oe-status-dot${isLive ? ' oe-status-dot--pulse' : ''}`} />
        <span>{statusLabel}</span>
      </div>
    </div>
  );
}

export default function DashboardEventsPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const authReady = useAuthReady();
  const { user } = useAuthStore();
  const role = getEffectiveRole(user);
  const isOrgStaff = role === 'organization_admin' || role === 'organization_manager';
  const canUseOrganizerApis = canUseOrganizerDashboardApis(user);

  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  const rawFilterParam = searchParams?.get('filter') || searchParams?.get('tab');
  useEffect(() => {
    if (!rawFilterParam) return;
    const norm = rawFilterParam.toLowerCase();
    if (norm === 'draft' || norm === 'drafts' || norm === 'saved_drafts') {
      setActiveFilter('draft');
    } else if (norm === 'active' || norm === 'live' || norm === 'live_events') {
      setActiveFilter('active');
    } else if (norm === 'past' || norm === 'past_events') {
      setActiveFilter('past');
    } else if (norm === 'all') {
      setActiveFilter('all');
    }
  }, [rawFilterParam]);

  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<PaginationMeta>({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 12,
  });

  const loadEvents = useCallback(async () => {
    if (!canUseOrganizerApis) {
      router.replace('/dashboard/my-events');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await eventsApi.myEvents({ per_page: 12, page });
      const payload = res.data?.data;
      const list = Array.isArray(payload)
        ? payload
        : payload?.data ?? (Array.isArray(res.data) ? res.data : []);
      const uniqueEvents = Array.isArray(list)
        ? list.filter(
            (event: any, index: number, arr: any[]) =>
              arr.findIndex((candidate: any) => eventKey(candidate) === eventKey(event)) === index,
          )
        : [];
      setEvents(uniqueEvents);
      setMeta({
        current_page: Number(payload?.current_page ?? page),
        last_page: Number(payload?.last_page ?? 1),
        total: Number(payload?.total ?? uniqueEvents.length),
        per_page: Number(payload?.per_page ?? 12),
      });
    } catch (err) {
      setEvents([]);
      setError(getApiErrorMessage(err) || 'Unable to load your events.');
    } finally {
      setLoading(false);
    }
  }, [canUseOrganizerApis, page]);

  useEffect(() => {
    if (!authReady) return;

    if (isOrgStaff) {
      router.replace('/dashboard/organization/events');
      return;
    }

    void loadEvents();
  }, [authReady, isOrgStaff, loadEvents, router]);

  const formatDate = (start: string, end?: string) => {
    const s = new Date(start);
    if (end) {
      const e = new Date(end);
      return `${s.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return s.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const dateParts = (start?: string) => {
    if (!start) return { day: '—', month: 'TBD' };
    const d = new Date(start);
    if (Number.isNaN(d.getTime())) return { day: '—', month: 'TBD' };
    return {
      day: String(d.getDate()).padStart(2, '0'),
      month: d.toLocaleDateString('en-IN', { month: 'short' }).toUpperCase(),
    };
  };

  // Use real events from backend
  const combinedList = useMemo(() => {
    return events;
  }, [events]);

  // Compute tab counts and global hero metrics
  const { filterCounts, heroStats } = useMemo(() => {
    const now = Date.now();
    let totalRegs = 0;
    let active = 0;
    let draft = 0;
    let past = 0;

    for (const e of combinedList) {
      const status = (e.status || '').toLowerCase();
      const end = e.end_date ? new Date(e.end_date).getTime() : Infinity;
      const isPast = end < now || status === 'completed' || status === 'cancelled';
      const isDraft = status === 'draft';
      const isActive = !isPast && !isDraft && status !== 'cancelled';

      totalRegs += e.registrations_count ?? 0;
      if (isActive) active++;
      if (isDraft) draft++;
      if (isPast) past++;
    }

    return {
      filterCounts: {
        all: combinedList.length,
        active,
        draft,
        past,
      },
      heroStats: {
        total: meta.total > 0 ? meta.total : combinedList.length,
        active,
        draft,
        totalRegs,
      },
    };
  }, [combinedList, meta.total]);

  // Filter and sort events
  const displayEvents = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();

    const filtered = combinedList.filter((event) => {
      const matchesSearch =
        !q ||
        event.title?.toLowerCase().includes(q) ||
        event.venue_city?.toLowerCase().includes(q) ||
        event.venue_name?.toLowerCase().includes(q) ||
        event.category?.toLowerCase().includes(q);

      const status = (event.status || '').toLowerCase();
      const end = event.end_date ? new Date(event.end_date).getTime() : Infinity;
      const isPast = end < now || status === 'completed' || status === 'cancelled';
      const isDraft = status === 'draft';
      const isActive = !isPast && !isDraft && status !== 'cancelled';

      if (!matchesSearch) return false;

      if (activeFilter === 'active') return isActive;
      if (activeFilter === 'draft') return isDraft;
      if (activeFilter === 'past') return isPast;
      return true; // 'all'
    });

    // Sorting
    return filtered.sort((a, b) => {
      if (sortBy === 'title') {
        return (a.title || '').localeCompare(b.title || '');
      }
      if (sortBy === 'registrations') {
        return (b.registrations_count ?? 0) - (a.registrations_count ?? 0);
      }
      if (sortBy === 'date') {
        const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
        const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;
        return dateA - dateB;
      }
      // 'newest' default
      const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
      if (timeA && timeB) return timeB - timeA;
      const startA = a.start_date ? new Date(a.start_date).getTime() : 0;
      const startB = b.start_date ? new Date(b.start_date).getTime() : 0;
      return startB - startA;
    });
  }, [combinedList, search, activeFilter, sortBy]);

  const filters: { id: FilterType; label: string; count: number }[] = [
    { id: 'all', label: 'All', count: filterCounts.all },
    { id: 'active', label: 'Active', count: filterCounts.active },
    { id: 'draft', label: 'Drafts', count: filterCounts.draft },
    { id: 'past', label: 'Past', count: filterCounts.past },
  ];

  return (
    <div className="org-account-page">
      {/* ── Header Banner ── */}
      <header className="org-finance-hero">
        <div className="org-finance-hero-glow" aria-hidden />
        <div className="org-finance-hero-top">
          <div>
            <p className="org-finance-hero-kicker">Organizer Workspace</p>
            <h1 className="org-finance-hero-title">My Events</h1>
            <p className="org-finance-hero-desc">
              Manage registrations, configure ticketing, and launch workspaces for your events.
            </p>
          </div>
          <div className="oe-header-actions">
            <Link href="/dashboard/events/new" className="org-finance-hero-btn">
              <Plus className="w-4 h-4" /> Create Event
            </Link>
          </div>
        </div>
      </header>

      {/* ── Toolbar / Controls ── */}
      <div className="oe-toolbar-wrap">
        <div className="oe-filters" role="tablist" aria-label="Filter events">
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={f.id === activeFilter}
              onClick={() => setActiveFilter(f.id)}
              className={`oe-filter ${f.id === activeFilter ? 'oe-filter--active' : ''}`}
            >
              {f.label}
              <span className="oe-filter-badge">{f.count}</span>
            </button>
          ))}
        </div>

        <div className="oe-controls-group">
          {/* Search bar with clear button */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)] pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events…"
              className="org-account-input pl-9 pr-8 !h-9 !text-xs"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text)] p-0.5"
                title="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            ) : null}
          </div>

          {/* Sort dropdown */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="oe-select-sort"
            aria-label="Sort events"
          >
            <option value="newest">Newest First</option>
            <option value="date">Event Date (Soonest)</option>
            <option value="registrations">Most Registrations</option>
            <option value="title">Title (A – Z)</option>
          </select>

          {/* View toggle (Grid / List) */}
          <div className="oe-view-toggle" role="group" aria-label="View format">
            <button
              type="button"
              title="Grid view"
              aria-label="Grid view"
              onClick={() => setViewMode('grid')}
              className={`oe-view-btn ${viewMode === 'grid' ? 'oe-view-btn--active' : ''}`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              title="List view"
              aria-label="List view"
              onClick={() => setViewMode('list')}
              className={`oe-view-btn ${viewMode === 'list' ? 'oe-view-btn--active' : ''}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Content View ── */}
      {!authReady || loading ? (
        <OrganizerLoadingState label="Loading your events…" />
      ) : (
        <>
          {error ? (
            <div className="mb-4">
              <OrganizerErrorState message={error} onRetry={() => void loadEvents()} />
            </div>
          ) : null}

          {displayEvents.length === 0 ? (
            <div className="oe-empty-state">
              <div className="oe-empty-icon-wrap">
                <CalendarDays className="w-7 h-7" />
              </div>
              <h3 className="oe-empty-title">No events found</h3>
              <p className="oe-empty-desc">
                {search
                  ? `No events matching "${search}". Try clearing your search term or selecting another filter.`
                  : 'You do not have any events in this category yet. Click below to create your first event!'}
              </p>
              {search ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setActiveFilter('all');
                  }}
                  className="oe-empty-btn"
                >
                  <X className="w-4 h-4" /> Clear Search & Filters
                </button>
              ) : (
                <Link href="/dashboard/events/new" className="oe-empty-btn">
                  <Plus className="w-4 h-4" /> Create New Event
                </Link>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            /* ── Modern Card Grid ── */
            <div className="oe-event-grid">
              {displayEvents.map((event, index) => {
                const id = eventKey(event);
                const regs = event.registrations_count ?? 0;
                const date = dateParts(event.start_date);
                const venue = event.venue_city || event.venue_name || 'Venue TBD';
                const isDummy = Boolean(event.__dummy);
                const rawStatus = (event.status || '').toLowerCase();
                const now = Date.now();
                const end = event.end_date ? new Date(event.end_date).getTime() : Infinity;
                const isPast = end < now || rawStatus === 'completed' || rawStatus === 'cancelled';

                // Status pill configuration
                let statusClass = 'oe-status-pill--published';
                let statusLabel = 'Published';
                let isLive = false;

                if (isDummy) {
                  statusClass = 'oe-status-pill--preview';
                  statusLabel = 'Preview';
                } else if (rawStatus === 'draft') {
                  statusClass = 'oe-status-pill--draft';
                  statusLabel = 'Draft';
                } else if (isPast) {
                  statusClass = 'oe-status-pill--past';
                  statusLabel = 'Past';
                } else {
                  statusClass = 'oe-status-pill--published';
                  statusLabel = 'Live';
                  isLive = true;
                }

                const modeLabel =
                  event.mode === 'in_person'
                    ? 'In-Person'
                    : event.mode === 'online'
                      ? 'Virtual'
                      : event.mode === 'hybrid'
                        ? 'Hybrid'
                        : event.category || null;

                const cardClass = `oe-event-card group${isDummy ? ' oe-event-card--dummy' : ''}`;

                const cardInner = (
                  <>
                    <EventCardCover
                      event={event}
                      date={date}
                      statusClass={statusClass}
                      statusLabel={statusLabel}
                      isLive={isLive}
                    />

                    {/* Card Content */}
                    <div className="oe-card-content">
                      {modeLabel ? (
                        <div className="oe-card-tag-row">
                          <span className="oe-card-tag">{modeLabel}</span>
                        </div>
                      ) : null}

                      <h3 className="oe-card-title">{event.title}</h3>

                      <div className="oe-card-meta">
                        <div className="oe-card-meta-item">
                          <Calendar className="oe-card-meta-icon" aria-hidden />
                          <span className="truncate">{formatDate(event.start_date, event.end_date)}</span>
                        </div>
                        <div className="oe-card-meta-item">
                          <MapPin className="oe-card-meta-icon" aria-hidden />
                          <span className="truncate">{venue}</span>
                        </div>
                      </div>

                      {/* Card Footer */}
                      <div className="oe-card-footer">
                        <span className="oe-card-regs">
                          <Ticket className="oe-card-regs-icon" aria-hidden />
                          {regs} registration{regs === 1 ? '' : 's'}
                        </span>
                        <span className="oe-card-cta">
                          {isDummy ? 'Preview' : 'Workspace'}
                          <ArrowUpRight className="oe-card-cta-icon" aria-hidden />
                        </span>
                      </div>
                    </div>
                  </>
                );

                if (isDummy) {
                  return (
                    <div key={`${id}-${index}`} className={cardClass}>
                      {cardInner}
                    </div>
                  );
                }

                return (
                  <Link
                    key={`${id}-${index}`}
                    href={`/dashboard/events/${id}`}
                    className={cardClass}
                  >
                    {cardInner}
                  </Link>
                );
              })}
            </div>
          ) : (
            /* ── Compact List View ── */
            <div className="oe-list-container">
              {displayEvents.map((event, index) => {
                const id = eventKey(event);
                const regs = event.registrations_count ?? 0;
                const date = dateParts(event.start_date);
                const venue = event.venue_city || event.venue_name || 'Venue TBD';
                const isDummy = Boolean(event.__dummy);
                const rawStatus = (event.status || '').toLowerCase();
                const now = Date.now();
                const end = event.end_date ? new Date(event.end_date).getTime() : Infinity;
                const isPast = end < now || rawStatus === 'completed' || rawStatus === 'cancelled';

                let statusClass = 'oe-status-pill--published';
                let statusLabel = 'Published';
                let isLive = false;

                if (isDummy) {
                  statusClass = 'oe-status-pill--preview';
                  statusLabel = 'Preview';
                } else if (rawStatus === 'draft') {
                  statusClass = 'oe-status-pill--draft';
                  statusLabel = 'Draft';
                } else if (isPast) {
                  statusClass = 'oe-status-pill--past';
                  statusLabel = 'Past';
                } else {
                  statusClass = 'oe-status-pill--published';
                  statusLabel = 'Live';
                  isLive = true;
                }

                const listInner = (
                  <>
                    <div className="oe-list-row-main">
                      <div className="oe-list-date-mini" aria-hidden>
                        <span className="oe-list-date-mon">{date.month}</span>
                        <span className="oe-list-date-day">{date.day}</span>
                      </div>
                      <div className="oe-list-info">
                        <h3 className="oe-list-title">{event.title}</h3>
                        <div className="oe-list-meta">
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5 shrink-0" />
                            {formatDate(event.start_date, event.end_date)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5 shrink-0" />
                            {venue}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="oe-list-row-side">
                      <div className={`oe-status-pill ${statusClass}`}>
                        <span className={`oe-status-dot${isLive ? ' oe-status-dot--pulse' : ''}`} />
                        <span>{statusLabel}</span>
                      </div>
                      <span className="oe-card-regs">
                        <Ticket className="oe-card-regs-icon" />
                        {regs} regs
                      </span>
                      <span className="oe-card-cta">
                        {isDummy ? 'Preview' : 'Open'}
                        <ArrowUpRight className="oe-card-cta-icon" />
                      </span>
                    </div>
                  </>
                );

                if (isDummy) {
                  return (
                    <div key={`${id}-${index}`} className="oe-list-row opacity-80 cursor-default">
                      {listInner}
                    </div>
                  );
                }

                return (
                  <Link
                    key={`${id}-${index}`}
                    href={`/dashboard/events/${id}`}
                    className="oe-list-row group"
                  >
                    {listInner}
                  </Link>
                );
              })}
            </div>
          )}

          {/* ── Pagination ── */}
          {meta.last_page > 1 ? (
            <div className="flex items-center justify-between gap-3 pt-4">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-bg px-3.5 py-2 text-sm font-medium text-[var(--color-text)] shadow-sm hover:bg-[var(--color-surface-muted)] disabled:opacity-40 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>
              <p className="text-sm font-medium text-[var(--color-text-muted)]">
                Page {meta.current_page} of {meta.last_page}
              </p>
              <button
                type="button"
                disabled={page >= meta.last_page || loading}
                onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-bg px-3.5 py-2 text-sm font-medium text-[var(--color-text)] shadow-sm hover:bg-[var(--color-surface-muted)] disabled:opacity-40 transition-colors"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
